import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyMessage?: string;
  toolbar?: React.ReactNode;
  /** کلاس‌ها برای ظرف کنارِ جدول (مثلاً فاصله بین فیلترها) */
  toolbarClassName?: string;
}

/** جدول مبتنی بر TanStack Table؛ راست‌چین؛ اسکرول افقی برای موبایل */
export default function DataTable<TData, TValue>({
  columns,
  data,
  emptyMessage = 'رکوردی وجود ندارد',
  toolbar,
  toolbarClassName,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;

  return (
    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-6">
      {toolbar && (
        <div className={cn('flex flex-wrap items-end gap-y-4 gap-x-3', toolbarClassName)}>{toolbar}</div>
      )}
      <div className="flex min-h-[12rem] min-w-0 w-full flex-1 flex-col overflow-hidden rounded-md border border-border bg-card shadow-sm">
        <div className="min-h-0 min-w-0 w-full flex-1 overflow-x-auto overflow-y-auto" dir="rtl">
          <Table className="w-full min-w-[52rem]">
            <TableHeader className="sticky top-0 z-[1] bg-muted/95 backdrop-blur">
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <TableHead key={h.id} className="whitespace-nowrap">
                      {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {rows.length ? (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
